// frontend/src/components/OAuthLoginButton.jsx

import React, { useState } from 'react';
import { Button, CircularProgress, Typography, Box, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import GoogleIcon from '@mui/icons-material/Google';
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import BusinessIcon from '@mui/icons-material/Business';
import { startOAuthFlow } from '../api/oauthApi.js';

const OAuthButton = styled(Button)(({ theme, provider, isSmall }) => {
  const providerColors = {
    google: {
      backgroundColor: theme.palette.mode === 'dark' ? '#5a95f5' : '#4285f4',
      '&:hover': {
        backgroundColor: theme.palette.mode === 'dark' ? '#4b7fe8' : '#3367d6',
      },
    },
    microsoft: {
      backgroundColor: theme.palette.mode === 'dark' ? '#2b8ce5' : '#0078d4',
      '&:hover': {
        backgroundColor: theme.palette.mode === 'dark' ? '#1a7dd4' : '#106ebe',
      },
    },
    generic: {
      backgroundColor: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  };

  return {
    minWidth: isSmall ? 240 : 280,
    height: isSmall ? 36 : 48,
    borderRadius: isSmall ? 6 : 8,
    textTransform: 'none',
    fontSize: isSmall ? '14px' : '16px',
    fontWeight: 500,
    color: 'white',
    boxShadow: theme.shadows[2],
    '&:hover': {
      boxShadow: theme.shadows[4],
    },
    ...(providerColors[provider] || providerColors.generic),
  };
});

function ProviderIcon({ provider, size = 24 }) {
  const iconProps = { style: { fontSize: size, marginRight: 12 } };

  switch (provider) {
    case 'google':
      return <GoogleIcon {...iconProps} />;
    case 'microsoft':
      return <MicrosoftIcon {...iconProps} />;
    default:
      return <BusinessIcon {...iconProps} />;
  }
}

const getProviderDisplayName = provider => {
  const displayNames = {
    google: 'Google',
    microsoft: 'Microsoft',
    azure: 'Azure AD',
    okta: 'Okta',
    auth0: 'Auth0',
    keycloak: 'Keycloak',
  };

  return displayNames[provider] || provider.charAt(0).toUpperCase() + provider.slice(1);
};

/**
 * OAuth Login Button Component
 * Handles OAuth authentication flow with proper branding
 */
function OAuthLoginButton({
  provider,
  displayName,
  disabled = false,
  fullWidth = true,
  variant = 'contained',
  size = 'large',
  onSuccess,
  onError,
  redirectUri,
  options = {},
  children,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleOAuthLogin = async () => {
    setLoading(true);
    setError(null);

    try {
      // Generate redirect URI if not provided
      const finalRedirectUri = redirectUri || `${window.location.origin}/oauth/callback`;

      // Start OAuth flow
      const authResult = await startOAuthFlow(provider, finalRedirectUri, options);

      // Store state for callback validation
      sessionStorage.setItem('oauth_state', authResult.state);
      sessionStorage.setItem('oauth_provider', authResult.provider);

      // Redirect to OAuth provider
      window.location.href = authResult.authorizationUrl;
    } catch (error) {
      console.error('OAuth login failed:', error);
      setError(error.message);
      setLoading(false);

      if (onError) {
        onError(error);
      }
    }
  };

  const providerName = displayName || getProviderDisplayName(provider);

  if (error) {
    return (
      <Box sx={{ width: fullWidth ? '100%' : 'auto', mb: 2 }}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Box>
    );
  }

  const isSmall = options?.buttonSize === 'small';
  const iconSize = isSmall ? 20 : 24;

  return (
    <OAuthButton
      provider={provider}
      isSmall={isSmall}
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled || loading}
      onClick={handleOAuthLogin}
      startIcon={
        loading ? (
          <CircularProgress size={isSmall ? 16 : 20} color="inherit" />
        ) : (
          <ProviderIcon provider={provider} size={iconSize} />
        )
      }
    >
      {loading ? (
        <Typography variant="button" fontSize={isSmall ? '0.875rem' : '1rem'}>
          Connecting to {providerName}...
        </Typography>
      ) : (
        children || (
          <Typography variant="button" fontSize={isSmall ? '0.875rem' : '1rem'}>
            Continue with {providerName}
          </Typography>
        )
      )}
    </OAuthButton>
  );
}

export default OAuthLoginButton;
